import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchOssOrderDetail } from "../../api/ossApi";
import { displayLabel, orderStatus, riskLevel, scheduleStatus, sourceType } from "../../constants/labels";
import { OssStatusChip } from "../OssStatusChip";
import { getLineActionReason, isActionNeeded } from "../orderDetail/detailUtils";

const SummaryTile = ({ label, value, helper, tone = "#111827" }) => (
  <Box
    sx={{
      minHeight: 82,
      p: 1.65,
      border: `1px solid ${tone}24`,
      borderRadius: "8px",
      bgcolor: "#fff",
    }}
  >
    <Typography sx={{ fontSize: 12, color: "#64748b" }}>{label}</Typography>
    <Typography sx={{ mt: 0.35, fontSize: 22, lineHeight: 1.05, fontWeight: 900, color: tone }}>
      {value || "-"}
    </Typography>
    {helper && <Typography noWrap sx={{ mt: 0.35, fontSize: 11.5, color: "#64748b" }}>{helper}</Typography>}
  </Box>
);

const DetailField = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 12, color: "#64748b" }}>{label}</Typography>
    <Typography noWrap sx={{ mt: 0.35, fontSize: 13.5, color: "#111827" }}>
      {value || "-"}
    </Typography>
  </Box>
);

const LineSummaryCard = ({ line, disabledByCancel }) => {
  const riskMeta = riskLevel[line.risk_level] || riskLevel.NORMAL;
  const actionNeeded = isActionNeeded(line) && !disabledByCancel;

  return (
    <Box
      sx={{
        p: 1.65,
        border: "1px solid #e5e7eb",
        borderLeft: `4px solid ${riskMeta.tone}`,
        borderRadius: "8px",
        bgcolor: "#fff",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }} justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 15, fontWeight: 900, color: "#111827" }}>
            {line.item_name_snapshot}
          </Typography>
          <Typography noWrap sx={{ mt: 0.2, fontSize: 12, color: "#64748b" }}>
            {line.item_code_snapshot || "품목코드 미등록"} · {line.specification_snapshot || "규격 미등록"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ flexShrink: 0 }}>
          <OssStatusChip value={line.schedule_status} />
          <OssStatusChip type="risk" value={line.risk_level} />
        </Stack>
      </Stack>

      <Box
        sx={{
          mt: 1.45,
          pt: 1.25,
          borderTop: "1px solid #eef2f7",
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(6, minmax(0, 1fr))" },
          gap: 1.45,
        }}
      >
        <DetailField label="수량" value={`${Number(line.order_quantity || 0).toLocaleString()} ${line.unit_code || ""}`} />
        <DetailField label="요청 납기" value={line.requested_due_date} />
        <DetailField label="착수 필요일" value={line.required_start_date} />
        <DetailField label="확인 필요일" value={line.check_required_date} />
        <DetailField label="소요일/여유일" value={`${line.standard_lead_days ?? "-"}일 / ${line.planning_buffer_days ?? "-"}일`} />
        <DetailField label="남은 여유일" value={line.available_margin_days ?? "-"} />
      </Box>

      {actionNeeded && (
        <Alert
          severity={line.risk_level === "OVERDUE" || line.risk_level === "HIGH" ? "error" : "warning"}
          icon={false}
          sx={{
          mt: 1.2,
          py: 0.65,
          px: 1.1,
            borderRadius: "8px",
            "& .MuiAlert-message": { py: 0, fontSize: 12.5 },
          }}
        >
          {getLineActionReason(line, riskLevel, scheduleStatus)} 항목입니다. 조치 완료 시 우선 조치 목록에서 제외됩니다.
        </Alert>
      )}
    </Box>
  );
};

export const OssOrderInlineDetail = ({ open, groupCode, projectCode, orderId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!open || !orderId) return;
    setLoading(true);
    setError("");
    setData(null);
    try {
      setData(await fetchOssOrderDetail(groupCode, projectCode, orderId));
    } catch (e) {
      setError(e.response?.data?.message || "주문 상세를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [groupCode, open, orderId, projectCode]);

  useEffect(() => {
    load();
  }, [load]);

  const order = data?.order || {};
  const lines = useMemo(() => data?.lines || [], [data?.lines]);
  const summary = useMemo(() => {
    const minMargin = lines
      .map((line) => Number(line.available_margin_days))
      .filter(Number.isFinite)
      .sort((a, b) => a - b)[0];

    return {
      lineCount: lines.length,
      actionCount: lines.filter(isActionNeeded).length,
      earliestStart: lines.map((line) => line.required_start_date).filter(Boolean).sort()[0],
      earliestCheck: lines.map((line) => line.check_required_date).filter(Boolean).sort()[0],
      minMargin: minMargin ?? "-",
    };
  }, [lines]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          overflow: "hidden",
          maxWidth: 940,
          bgcolor: "#f3f6fa",
          boxShadow: "0 18px 45px rgba(15, 23, 42, 0.18)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: "20px !important",
          py: "16px !important",
          pr: 6.2,
          borderBottom: "1px solid #e5e7eb",
          bgcolor: "#fff",
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              display: "grid",
              placeItems: "center",
              borderRadius: "8px",
              bgcolor: "#eef2ff",
              color: "#6c5ce7",
              flexShrink: 0,
            }}
          >
            <Inventory2OutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 19, lineHeight: 1.2, fontWeight: 900, color: "#111827" }}>
                {order.order_no || "주문 상세"}
              </Typography>
              {order.order_status && (
                <Box
                  sx={{
                    px: 0.85,
                    py: 0.25,
                    borderRadius: "999px",
                    bgcolor: "#eef2ff",
                    color: "#4f46e5",
                    fontSize: 11.5,
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                >
                  {displayLabel(orderStatus, order.order_status)}
                </Box>
              )}
            </Stack>
            <Typography noWrap sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b" }}>
              {order.customer_name ? `${order.customer_name} · ${order.site_name || "납품처 미등록"}` : "주문 정보를 불러오는 중입니다."}
            </Typography>
          </Box>
        </Stack>
        <Button
          color="inherit"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            minWidth: 34,
            width: 34,
            height: 34,
            p: 0,
            color: "#6b7280",
          }}
        >
          <CloseRoundedIcon />
        </Button>
      </DialogTitle>

      <DialogContent
        sx={{
          p: "18px !important",
          bgcolor: "#eef2f7",
        }}
      >
        {loading ? (
          <Box sx={{ minHeight: 360, display: "grid", placeItems: "center", borderRadius: "8px", bgcolor: "#fff" }}>
            <CircularProgress size={30} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2, borderRadius: "8px", bgcolor: "#fff" }}>
            <Alert severity="warning">{error}</Alert>
          </Box>
        ) : data ? (
          <Stack
            spacing={1.25}
            sx={{
              maxHeight: "min(68vh, 660px)",
              overflow: "auto",
              pr: 0.75,
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { width: 8 },
              "&::-webkit-scrollbar-thumb": { borderRadius: 999, backgroundColor: "#cbd5e1" },
              "&::-webkit-scrollbar-track": { backgroundColor: "#eef2f7" },
            }}
          >
            <Box sx={{ p: 1.7, border: "1px solid #e5e7eb", borderRadius: "8px", bgcolor: "#fff" }}>
              <Typography sx={{ mb: 1.25, fontSize: 15.5, fontWeight: 900, color: "#111827" }}>주문 기본 정보</Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
                  columnGap: 2.2,
                  rowGap: 1.3,
                }}
              >
                <DetailField label="고객 발주번호" value={order.customer_po_no} />
                <DetailField label="주문일자" value={order.order_date} />
                <DetailField label="입력 방식" value={displayLabel(sourceType, order.source_type)} />
                <DetailField label="최근 수정" value={order.updated_at} />
              </Box>
              {(order.manager_user_key || order.note) && (
                <>
                  <Divider sx={{ my: 1.35 }} />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "minmax(0, 0.9fr) minmax(0, 2fr)" },
                      gap: 1.6,
                    }}
                  >
                    <DetailField label="담당자" value={order.manager_user_key} />
                    <DetailField label="비고" value={order.note} />
                  </Box>
                </>
              )}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
                gap: 1,
              }}
            >
              <SummaryTile label="관리 품목" value={`${summary.lineCount}건`} helper="주문 품목 기준" tone="#6c5ce7" />
              <SummaryTile label="조치 필요" value={`${summary.actionCount}건`} helper="처리 후 제외" tone={summary.actionCount ? "#ef4444" : "#14b8a6"} />
              <SummaryTile label="가장 빠른 착수일" value={summary.earliestStart} tone="#2563eb" />
              <SummaryTile label="최소 여유일" value={`${summary.minMargin}일`} helper={`확인일 ${summary.earliestCheck || "-"}`} tone={Number(summary.minMargin) < 0 ? "#ef4444" : "#f59e0b"} />
            </Box>

            <Box sx={{ p: 1.7, border: "1px solid #e5e7eb", borderRadius: "8px", bgcolor: "#fff" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.15 }}>
                <Typography sx={{ fontSize: 15.5, fontWeight: 900, color: "#111827" }}>품목별 일정 요약</Typography>
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>{lines.length}개 품목</Typography>
              </Stack>
              <Box
                sx={{
                  mb: 1.1,
                  px: 1.2,
                  py: 1,
                  borderRadius: "8px",
                  border: "1px solid #dbe4f0",
                  bgcolor: "#f8fafc",
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                }}
              >
                <InfoOutlinedIcon sx={{ mt: 0.1, fontSize: 18, color: "#64748b", flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 12.5, color: "#475569" }}>
                    왼쪽 칩은 업무 진행 상태입니다. 확인 필요, 변경 검토, 계획 확정처럼 담당자가 처리할 흐름을 구분합니다.
                  </Typography>
                  <Typography sx={{ mt: 0.35, fontSize: 12.5, color: "#475569" }}>
                    오른쪽 칩은 납기 기준 위험도입니다. 착수 필요일까지 남은 여유일을 기준으로 정상, 주의, 고위험, 기한 초과를 표시합니다.
                  </Typography>
                </Box>
              </Box>
              <Stack spacing={0.85}>
                {lines.map((line) => (
                  <LineSummaryCard key={line.order_line_id} line={line} disabledByCancel={order.order_status === "CANCELLED"} />
                ))}
              </Stack>
            </Box>
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          px: "18px !important",
          py: "12px !important",
          minHeight: 56,
          alignItems: "center",
          justifyContent: "flex-end",
          borderTop: "1px solid #e5e7eb",
          bgcolor: "#fff",
        }}
      >
        <Button variant="contained" onClick={onClose} sx={{ minWidth: 84, height: 36 }}>
          확인
        </Button>
      </DialogActions>
    </Dialog>
  );
};
